import React, { PropsWithChildren, useState } from 'react';
import { TitleModalHeader } from 'features/offline-reports/support-requests/components/support-requests-modals/components/title-modal-header/title-modal-header';
import { SupportRequestModalContent } from 'features/offline-reports/support-requests/components/support-requests-modals/components/location-modal-content/support-request-modal-content';
import { Button, Modal, SimpleTooltip } from '@placer-ui/components';
import { DefaultValues, FieldValues, FormProvider, useForm } from 'react-hook-form';
import { SubmitSupportRequestPayload } from 'API/offline-reports-api/types';
import { FormControl, SupportRequestType } from '../types';
import { useOfflineReportsActions } from 'store/offline-reports/use-offline-reports-actions';
import { supportApi } from 'API/offline-reports-api';
import { useSendSupportSplunkEvent } from 'features/offline-reports/support-requests/hooks/use-send-support-splunk-event';
import { getFeatureFlags } from 'core/flow-control';
import { useOpenSupportRequest } from 'features/offline-reports/support-requests/components/support-requests-modals/support-modal-manager';

const FILL_MANDATORY_TOOLTIP = 'Please fill all mandatory fields to proceed.';
const SALES_TOOLTIP = 'Sales are not allowed to open a chain ticket';

export interface SupportRequestFormProps<T extends FieldValues = FieldValues> {
    title?: string;
    subTitle?: string;
    form?: FormControl<T>;
    defaultValues?: Partial<T>;
    withBackButton?: boolean;
    withCloseButton?: boolean;
    handleBack?: () => void;
    postSubmitAction?: (caseId: string) => void;
    convertToRequestPayload: (data: T) => SubmitSupportRequestPayload;
    testId?: string;
    requestType: SupportRequestType;
}

export function SupportRequestModalForm<T extends FieldValues = FieldValues>({
    form,
    convertToRequestPayload,
    handleBack,
    postSubmitAction,
    withBackButton = true,
    withCloseButton = false,
    title,
    subTitle,
    children,
    defaultValues = {},
    testId,
    requestType,
}: PropsWithChildren<SupportRequestFormProps<T>>) {
    const [isSendingRequest, setIsSendingRequest] = useState(false);
    const { getSupportRequests } = useOfflineReportsActions();
    const sendSplunkEvent = useSendSupportSplunkEvent();
    const { enable_request_properties_sales_flow_ff } = getFeatureFlags();
    const { closeSupportRequest, openSupportRequest } = useOpenSupportRequest();

    const defaultForm = useForm<T>({
        defaultValues: {
            ...(defaultValues as DefaultValues<T>),
        },
        mode: 'all',
    });

    const formControl = form ?? defaultForm;

    const handleSubmitFailure = () => {
        setIsSendingRequest(false);

        openSupportRequest({
            flowType: 'failed',
            retryType: requestType,
            props: formControl.getValues(),
        });
    };

    const defaultHandleBack = () => {
        openSupportRequest({ flowType: 'typeSelection' });
    };

    const defaultHandleSubmit = async (data: T) => {
        setIsSendingRequest(true);
        sendSplunkEvent({
            action: 'submit-support-request',
            value: requestType,
        });

        try {
            const response = await supportApi.submitRequest(convertToRequestPayload(data));

            if (response.result === 'SUCCESS') {
                postSubmitAction?.(response.case_id);
                formControl.reset();
                setIsSendingRequest(false);
                getSupportRequests();
                closeSupportRequest(requestType);
                openSupportRequest({ flowType: 'success' });
            } else {
                handleSubmitFailure();
            }
        } catch (e) {
            handleSubmitFailure();
        }
    };

    const isChainRequestDisabled =
        enable_request_properties_sales_flow_ff && requestType === 'chain';

    const tooltipText = isChainRequestDisabled ? SALES_TOOLTIP : FILL_MANDATORY_TOOLTIP;

    return (
        <FormProvider {...formControl}>
            <form
                onSubmit={formControl.handleSubmit(defaultHandleSubmit)}
                data-testid={testId}
                noValidate
                data-hs-cf-bound={false}
            >
                {title && <TitleModalHeader title={title} subTitle={subTitle} />}
                <SupportRequestModalContent testId="support-request-form">
                    {children}
                </SupportRequestModalContent>
                <Modal.Footer>
                    <Modal.Footer.RightControls>
                        {withBackButton && !withCloseButton && (
                            <Button
                                variant="secondary-blue"
                                fullWidth
                                onClick={handleBack ?? defaultHandleBack}
                                testId="support-request-back-button"
                            >
                                Back
                            </Button>
                        )}
                        {withCloseButton && !withBackButton && (
                            <Button
                                variant="secondary-blue"
                                fullWidth
                                onClick={() => closeSupportRequest(requestType)}
                                testId="support-request-close-button"
                            >
                                Close
                            </Button>
                        )}
                        <SimpleTooltip
                            appendToParent
                            title={tooltipText}
                            isActive={!formControl.formState.isValid || !!isChainRequestDisabled}
                        >
                            <Button
                                variant="primary-brand"
                                fullWidth
                                testId="support-request-submit-button"
                                disabled={
                                    !formControl.formState.isValid ||
                                    isSendingRequest ||
                                    isChainRequestDisabled
                                }
                                isLoading={isSendingRequest}
                            >
                                Submit
                            </Button>
                        </SimpleTooltip>
                    </Modal.Footer.RightControls>
                </Modal.Footer>
            </form>
        </FormProvider>
    );
}
